import { TZDate } from '@date-fns/tz';
import type { Interval } from 'date-fns';
import {
  addSeconds,
  format,
  getTime,
  isAfter,
  isBefore,
  isEqual,
  parseISO,
  startOfSecond,
} from 'date-fns';
import { notEmpty } from './array';

export function toDate(date: string | Date) {
  return typeof date === 'string' ? parseISO(date) : date;
}

/**
 * Parse a date string without timezone information as a date in the specified timezone.
 *
 * @param dateString - Date string in format "YYYY-MM-DDTHH:MM" or "YYYY-MM-DDTHH:MM:SS"
 * @param timeZone - IANA timezone identifier (e.g., "America/New_York", "Europe/London")
 * @returns Date object representing that time in the specified timezone
 * @throws Error if dateString contains timezone information or is in an invalid format
 */
export function parseInTimeZone(dateString: string, timeZone: TimeZone): Date {
  // Guard against strings that already contain timezone info
  if (
    dateString.includes('Z') ||
    dateString.includes('+') ||
    /[+-]\d{2}:\d{2}$/.test(dateString)
  ) {
    throw new Error(
      'parseInTimeZone expects a date string without timezone information. Use parseISO or new Date() instead.'
    );
  }

  const parts = dateString.split(/[-T:]/);

  const isDateOnly = parts.length === 3;
  const isDateTime = parts.length === 5 || parts.length === 6;

  if (!isDateOnly && !isDateTime) {
    throw new Error(
      `Invalid date string format: "${dateString}". Expected format: YYYY-MM-DD, YYYY-MM-DDTHH:MM or YYYY-MM-DDTHH:MM:SS`
    );
  }

  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]) - 1; // Month is 0-indexed
  const day = parseInt(parts[2]);
  const hour = isDateOnly ? 0 : parseInt(parts[3]);
  const minute = isDateOnly ? 0 : parseInt(parts[4]);
  const second = parts[5] ? parseInt(parts[5]) : 0;

  // Validate parsed values
  if (
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day) ||
    isNaN(hour) ||
    isNaN(minute) ||
    isNaN(second)
  ) {
    throw new Error(`Invalid date components in: "${dateString}"`);
  }

  return new TZDate(year, month, day, hour, minute, second, 0, timeZone);
}

/** IANA timezone identifier, e.g. "Europe/Stockholm" */
export type TimeZone = string;

export interface FormatDateOptions {
  dateStyle?: 'short' | 'shorter';
}

// short - hour and minute
// medium - hour, minute and second
// long - hour, minute, second and millisecond
export interface FormatTimeOptions {
  timeStyle?: 'short' | 'medium' | 'long';
}

export function formatDateTz(
  date: string | Date,
  timeZone: TimeZone,
  { dateStyle = 'short' }: FormatDateOptions = {}
) {
  const d = toDate(date);
  if (dateStyle === 'shorter') {
    return format(new TZDate(d, timeZone), 'MMM dd');
  }
  return d.toLocaleDateString(window.navigator.language, {
    dateStyle,
    timeZone,
  });
}

export function formatTimeTz(
  date: string | Date,
  timeZone: TimeZone,
  { timeStyle = 'short' }: FormatTimeOptions = {}
) {
  const d = toDate(date);
  if (timeStyle === 'long') {
    return d.toLocaleTimeString(window.navigator.language, {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      fractionalSecondDigits: 3,
      timeZone,
    });
  }
  return d.toLocaleTimeString(window.navigator.language, {
    timeStyle,
    timeZone,
  });
}

interface FormatOptions {
  dateOptions?: FormatDateOptions;
  timeOptions?: FormatTimeOptions;
}

export function formatDateAndTimeTz(
  date: string | Date,
  timeZone: TimeZone,
  { dateOptions, timeOptions }: FormatOptions = {}
) {
  return [
    formatDateTz(date, timeZone, dateOptions),
    formatTimeTz(date, timeZone, timeOptions),
  ]
    .filter(notEmpty)
    .join(' ')
    .trim();
}

export const userTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const formatDate = (date: string | Date, options?: FormatDateOptions) =>
  formatDateTz(date, userTimeZone(), options);

export const formatTime = (date: string | Date, options?: FormatTimeOptions) =>
  formatTimeTz(date, userTimeZone(), options);

export function formatDateAndTime(
  date: string | Date,
  options: FormatOptions = {}
) {
  return formatDateAndTimeTz(date, userTimeZone(), options);
}

export function formatMonth(date: string | Date) {
  return format(toDate(date), 'MMM');
}

export function isBeforeOrEqual(dateLeft: Date, dateRight: Date) {
  return isBefore(dateLeft, dateRight) || isEqual(dateLeft, dateRight);
}

export function isAfterOrEqual(dateLeft: Date, dateRight: Date) {
  return isAfter(dateLeft, dateRight) || isEqual(dateLeft, dateRight);
}

export function isBetween({
  date,
  from,
  to,
}: {
  date: Date | undefined | null;
  from: Date;
  to: Date;
}) {
  if (!date || isNaN(date.getTime())) {
    return false;
  }

  if (isNaN(from.getTime()) || isNaN(to.getTime())) {
    throw new Error("Invalid 'from' or 'to' date");
  }

  return date.getTime() >= from.getTime() && date.getTime() <= to.getTime();
}

interface StepOptions {
  step?: number;
}

export interface EachSecondOfIntervalOptions extends StepOptions {}

export function eachSecondOfInterval(
  interval: Interval,
  options?: EachSecondOfIntervalOptions
): Date[] {
  const startDate = startOfSecond(interval.start);
  const endDate = interval.end;

  const startTime = startDate.getTime();
  const endTime = typeof endDate === 'number' ? endDate : getTime(endDate);

  if (startTime === endTime) {
    return [startDate];
  }
  if (startTime > endTime) {
    throw new RangeError('Invalid interval');
  }

  const dates: Date[] = [];

  let currentDate = startDate;

  const step = options?.step ?? 1;
  if (step < 1 || isNaN(step))
    throw new RangeError(
      '`options.step` must be a number equal to or greater than 1'
    );

  while (currentDate.getTime() <= endTime) {
    dates.push(toDate(currentDate));
    currentDate = addSeconds(currentDate, step);
  }

  return dates;
}

interface ClosestDate {
  date: Date | undefined;
  diff: number;
}

export function findClosest(date: Date, dates: Date[]) {
  return dates.reduce<ClosestDate>(
    (closest, currentDate) => {
      const diff = Math.abs(date.getTime() - currentDate.getTime());
      return closest.diff > diff ? { diff, date: currentDate } : closest;
    },
    { date: undefined, diff: Infinity }
  );
}

export function exceedsAge(
  date: Date | string | undefined,
  age: number | undefined
) {
  if (date === undefined || age === undefined || !Number.isFinite(age)) {
    return false;
  }

  const now = Date.now();
  const sampleTime = new Date(date).getTime();

  return now - sampleTime > age * 1000;
}

export function getDuration(a: Date, b: Date) {
  return Math.abs(a.getTime() - b.getTime()) / 1000;
}
